import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { cx } from "../../lib/cx";
import { impact } from "../../native";
import {
  dropIndex,
  LIFT_HOLD_MS,
  LIFT_SLOP_PX,
  moved,
  type RowBox,
  shiftOf,
} from "./drag-list-math";
import { isGhostClick } from "./hold-decision";

/** Spread on the element that lifts its row at once, with no hold. */
export interface DragHandleProps {
  readonly className: string;
  readonly onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
}

export interface DragRowState {
  /** This row is the one under the finger. */
  readonly dragging: boolean;
  readonly handle: DragHandleProps;
  readonly index: number;
}

export interface DragListProps<T> {
  readonly className?: string;
  readonly items: readonly T[];
  readonly keyOf: (item: T) => string;
  /** The list's accessible name. */
  readonly label: string;
  /** The whole new order, once per drop — never while the finger moves. */
  readonly onReorder: (keys: readonly string[]) => void;
  readonly renderItem: (item: T, state: DragRowState) => ReactNode;
}

interface Lifted {
  readonly dy: number;
  readonly from: number;
  readonly key: string;
  /** How far a neighbour steps to make room, measured at lift. */
  readonly slot: number;
  readonly to: number;
}

/**
 * A vertical list whose rows are reordered by dragging (`D215`): hold a row
 * (320 ms) or press its handle, move it, let go. The rows between make room
 * as it passes, a light haptic marks each new place, and the drop calls
 * `onReorder` ONCE with the whole order — the plan's reorder takes every day
 * (`plan.reorder_sessions_incomplete`), so a pair to swap would be refused.
 *
 * ── DRAG IS NEVER THE ONLY WAY (WCAG 2.2 SC 2.5.7) ─────────────────────────
 * The caller renders a non-drag alternative in each row — the plan's ↑/↓.
 * That is why `D181` replaced the Expo app's drag with buttons; this puts the
 * drag back BESIDE them.
 *
 * ── A SCROLL STAYS A SCROLL ────────────────────────────────────────────────
 * A row is `touch-action: pan-y`, so a finger that moves before the hold
 * ends is the list scrolling: the browser takes the pointer
 * (`pointercancel`) and nothing lifts. Once a row HAS lifted, a non-passive
 * `touchmove` listener cancels the scroll — the finger has not moved yet,
 * so the first `touchmove` is still cancelable — and the pointer stays ours.
 * A handle is `touch-none` and lifts on the press itself.
 *
 * ── WINDOW LISTENERS, A GHOST CLICK, NO CONTEXT MENU ───────────────────────
 * `HoldToRecord`'s three reasons, unchanged: the row re-renders as it
 * lifts, so move/up/cancel live on `window`; the click the browser
 * synthesises after the release would land on a row's button, so the list
 * swallows a click inside `GHOST_CLICK_MS` of a drop; and a long press on
 * Android opens a menu, so `contextmenu` is prevented on the rows.
 *
 * ── THE DROPPED ORDER IS HELD UNTIL THE ITEMS CATCH UP ─────────────────────
 * The caller's write is asynchronous (an outbox write lands in the cache a
 * tick later). Between the drop and that tick the old order would draw for
 * one frame, so the list keeps drawing the dropped order until the items
 * arrive in it — or arrive with other keys, which means someone else
 * changed the list and their order wins.
 */
export function DragList<T>({
  className,
  items,
  keyOf,
  label,
  onReorder,
  renderItem,
}: DragListProps<T>) {
  const [lifted, setLifted] = useState<Lifted | null>(null);
  const [held, setHeld] = useState<readonly string[] | null>(null);
  const list = useRef<HTMLOListElement | null>(null);
  const boxes = useRef<readonly RowBox[]>([]);
  const guardAt = useRef(0);
  const teardown = useRef<(() => void) | null>(null);
  useEffect(() => () => teardown.current?.(), []);

  // The latest `onReorder`, for a gesture that outlives its render.
  const reorder = useRef(onReorder);
  useLayoutEffect(() => {
    reorder.current = onReorder;
  });

  const keys = items.map(keyOf);
  const order = heldOrder(keys, held);
  useEffect(() => {
    if (held !== null && order === keys) {
      setHeld(null);
    }
  }, [held, order, keys]);
  const byKey = new Map(items.map((item) => [keyOf(item), item] as const));
  const shown = order.flatMap((key) => {
    const item = byKey.get(key);
    return item === undefined ? [] : [item];
  });
  // The order on screen, for a gesture: read when it lifts and when it drops.
  const shownKeys = useRef<readonly string[]>(order);
  useLayoutEffect(() => {
    shownKeys.current = order;
  });

  // Not wrapped in `useCallback`: React Compiler memoises it, and a manual
  // wrapper it cannot reproduce makes it skip the whole component (`T-168`).
  const begin: Begin = (event, index, now) => {
    if (event.button > 0 || !event.isPrimary || teardown.current !== null) {
      return;
    }
    const { pointerId, clientX: startX, clientY: startY } = event;
    const key = shownKeys.current[index];
    if (key === undefined) {
      return;
    }
    let current: Lifted | null = null;

    const lift = () => {
      const rows = list.current?.querySelectorAll<HTMLElement>(
        ":scope > [data-drag-row]"
      );
      boxes.current = [...(rows ?? [])].map((row) => {
        const box = row.getBoundingClientRect();
        return { height: box.height, top: box.top };
      });
      current = {
        dy: 0,
        from: index,
        key,
        slot: slotOf(boxes.current, index),
        to: index,
      };
      setLifted(current);
      impact("medium");
    };

    const onMove = (move: PointerEvent) => {
      if (move.pointerId !== pointerId) {
        return;
      }
      const dy = move.clientY - startY;
      if (current === null) {
        // Moved before the hold: a scroll, not a drag.
        if (
          Math.abs(dy) > LIFT_SLOP_PX ||
          Math.abs(move.clientX - startX) > LIFT_SLOP_PX
        ) {
          teardown.current?.();
        }
        return;
      }
      const to = dropIndex(boxes.current, current.from, dy);
      if (to !== current.to) {
        impact("light");
      }
      current = { ...current, dy, to };
      setLifted(current);
    };

    const finish = (dropped: boolean) => {
      const was = current;
      teardown.current?.();
      if (was === null) {
        return;
      }
      guardAt.current = Date.now();
      setLifted(null);
      if (!dropped) {
        return;
      }
      const next = moved(shownKeys.current, was.from, was.to);
      if (next !== shownKeys.current) {
        setHeld(next);
        reorder.current(next);
      }
    };
    const onUp = (up: PointerEvent) => {
      if (up.pointerId === pointerId) {
        finish(true);
      }
    };
    const onCancel = (cancel: PointerEvent) => {
      if (cancel.pointerId === pointerId) {
        finish(false);
      }
    };
    // Only once lifted: before that a move is the list scrolling.
    const onTouchMove = (touch: TouchEvent) => {
      if (current !== null && touch.cancelable) {
        touch.preventDefault();
      }
    };

    const timer = now ? 0 : window.setTimeout(lift, LIFT_HOLD_MS);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    teardown.current = () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("touchmove", onTouchMove);
      teardown.current = null;
    };
    if (now) {
      lift();
    }
  };

  const swallowGhost = useCallback((event: ReactMouseEvent) => {
    if (isGhostClick(guardAt.current)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  return (
    <ol
      aria-label={label}
      className={cx("flex flex-col gap-2", className)}
      data-dragging={lifted === null ? undefined : ""}
      onClickCapture={swallowGhost}
      ref={list}
    >
      {shown.map((item, index) => {
        const dragging = lifted?.key === keyOf(item);
        return (
          <DragRow
            // biome-ignore lint/performance/noJsxPropsBind: React Compiler memoises `begin`; the `useCallback` this rule asks for is a manual memo it cannot preserve, and it then skips the whole component (`T-168`).
            begin={begin}
            dragging={dragging}
            index={index}
            key={keyOf(item)}
            moving={lifted !== null}
            offset={offsetOf(lifted, dragging, index)}
          >
            {(handle) => renderItem(item, { dragging, handle, index })}
          </DragRow>
        );
      })}
    </ol>
  );
}

type Begin = (
  event: ReactPointerEvent<HTMLElement>,
  index: number,
  now: boolean
) => void;

function DragRow({
  begin,
  children,
  dragging,
  index,
  moving,
  offset,
}: {
  begin: Begin;
  children: (handle: DragHandleProps) => ReactNode;
  dragging: boolean;
  index: number;
  moving: boolean;
  offset: number;
}) {
  const hold = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => begin(event, index, false),
    [begin, index]
  );
  const grab = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      event.stopPropagation();
      begin(event, index, true);
    },
    [begin, index]
  );
  const noMenu = useCallback(
    (event: ReactMouseEvent) => event.preventDefault(),
    []
  );
  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: the press only starts the drag gesture; every row carries its own ↑/↓ buttons, the keyboard and screen-reader way to do the same move (WCAG 2.2 SC 2.5.7).
    <li
      className={cx(
        "relative touch-pan-y select-none [-webkit-touch-callout:none]",
        moving &&
          !dragging &&
          "motion-safe:transition-transform motion-safe:duration-150",
        dragging && "z-10"
      )}
      data-drag-row=""
      data-lifted={dragging ? "" : undefined}
      onContextMenu={noMenu}
      onPointerDown={hold}
      style={
        offset === 0 && !dragging
          ? undefined
          : { transform: `translateY(${offset}px)` }
      }
    >
      {children({ className: "touch-none cursor-grab", onPointerDown: grab })}
    </li>
  );
}

/** How far a row is drawn from its place: under the finger, or making room. */
function offsetOf(
  lifted: Lifted | null,
  dragging: boolean,
  index: number
): number {
  if (lifted === null) {
    return 0;
  }
  return dragging
    ? lifted.dy
    : shiftOf(index, lifted.from, lifted.to, lifted.slot);
}

/** One row's height plus the gap to the next — how far a neighbour steps. */
function slotOf(rows: readonly RowBox[], from: number): number {
  const row = rows[from];
  if (row === undefined) {
    return 0;
  }
  const next = rows[from + 1] ?? rows[from - 1];
  return next === undefined ? row.height : Math.abs(next.top - row.top);
}

/**
 * The order to draw: the dropped one while the items have not caught up with
 * it, the items' own once they have — or once they hold other keys (someone
 * else changed the list). The SAME array as `keys` whenever it is theirs.
 */
function heldOrder(
  keys: readonly string[],
  held: readonly string[] | null
): readonly string[] {
  if (held === null || held.join("\u0000") === keys.join("\u0000")) {
    return keys;
  }
  const same =
    held.length === keys.length && held.every((key) => keys.includes(key));
  return same ? held : keys;
}
